// /admin/handoffs — operator console: who is waiting for a human, right now,
// across every client.
//
// Sorted oldest-first by the API, which is the order that matters: the top row is
// the person who has been ignored longest. Each row links into the conversation
// trace, and "Dejar con humano" toggles the sticky flag so the auto-return sweep
// leaves a delicate case alone.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import { returnConversationToBot } from './conversation-debug-data.js'
import {
  hoursParked,
  listParkedHandoffs,
  setHandoffSticky,
  urgencyOf,
  type ParkedHandoff,
} from './handoffs-data.js'
import {
  Button,
  Card,
  Chip,
  EmptyStateCard,
  PageHeader,
  Spinner,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'

const STATE_LABEL: Record<string, string> = {
  waiting_handoff: 'Esperando asesor',
  active_handoff: 'Con un asesor',
}

function ClockIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
      className="size-[18px]"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}

/** "hace 3 h" / "hace 25 min" — the age is the signal, so it leads the row. */
function ageLabel(h: ParkedHandoff): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(h.parkedSince).getTime()) / 60_000))
  if (mins < 60) return `${String(mins)} min`
  const hours = hoursParked(h.parkedSince)
  if (hours < 48) return `${String(hours)} h`
  return `${String(Math.floor(hours / 24))} d`
}

export function HandoffsPage(): JSX.Element {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'handoffs'],
    queryFn: listParkedHandoffs,
    // People are waiting; a stale list is the one thing this page must not show.
    refetchInterval: 60_000,
  })

  // Hand it back to the agent now, rather than waiting for the auto-return
  // sweep. Came from the unrouted ParkedHandoffs page this one replaced.
  const returnToAgent = useMutation({
    mutationFn: (id: string) => returnConversationToBot(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'handoffs'] })
      toast.ok('La conversación volvió al agente')
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'No se pudo regresar la conversación al agente.'))
    },
  })

  const sticky = useMutation({
    mutationFn: ({ id, next }: { id: string; next: boolean }) => setHandoffSticky(id, next),
    onSuccess: async (_r, vars) => {
      await qc.invalidateQueries({ queryKey: ['admin', 'handoffs'] })
      toast.ok(vars.next ? 'Se queda con un humano' : 'Puede volver al agente')
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'No se pudo cambiar la conversación.'))
    },
  })

  const waiting = data?.filter((h) => h.state === 'waiting_handoff').length ?? 0

  return (
    <div data-testid="admin-handoffs" className="mx-auto w-full max-w-5xl py-6">
      <PageHeader
        title="Conversaciones con personas"
        subtitle="Quién está esperando a un asesor ahora mismo, en todos los clientes."
      />

      <div className="mt-4">
        {isError ? (
          <QueryError
            message="No pudimos cargar las conversaciones en espera."
            onRetry={() => void refetch()}
          />
        ) : isLoading ? (
          <Card>
            <Card.Body>
              <Spinner label="Cargando…" />
            </Card.Body>
          </Card>
        ) : !data || data.length === 0 ? (
          <EmptyStateCard
            icon={<ClockIcon />}
            title="Nadie está esperando"
            body="Ninguna conversación está en manos de una persona ahora mismo. Cuando un agente pase una conversación a un asesor, aparecerá aquí."
          />
        ) : (
          <Card>
            <Card.Head
              title={`${String(data.length)} en manos de personas`}
              sub={
                waiting > 0
                  ? `${String(waiting)} sin que nadie las tome todavía. La más antigua va primero.`
                  : 'Todas están asignadas a un asesor.'
              }
            />
            <ul className="flex flex-col divide-y divide-line">
              {data.map((h) => {
                const tone = urgencyOf(h)
                return (
                  <li key={h.id} className="flex flex-wrap items-center gap-3 px-[18px] py-3">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-text">
                        {h.contact ?? 'Contacto sin nombre'}
                      </span>
                      <span className="mt-px block truncate text-[11px] text-text-3">
                        <Link
                          to={`/admin/tenants/${h.tenant.id}`}
                          className="underline-offset-2 hover:text-link hover:underline"
                        >
                          {h.tenant.name}
                        </Link>{' '}
                        · {STATE_LABEL[h.state] ?? h.state}
                      </span>
                    </span>
                    <Chip tone={tone} dot>
                      {ageLabel(h)}
                    </Chip>
                    <Button
                      variant={h.sticky ? 'secondary' : 'ghost'}
                      size="sm"
                      className="flex-none"
                      disabled={sticky.isPending}
                      onClick={() => sticky.mutate({ id: h.id, next: !h.sticky })}
                    >
                      {h.sticky ? 'Se queda con humano' : 'Dejar con humano'}
                    </Button>
                    {/* Only offered while a human still holds it — returning a
                        conversation nobody has taken is a no-op. */}
                    {h.state === 'active_handoff' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="flex-none"
                        disabled={returnToAgent.isPending}
                        onClick={() => returnToAgent.mutate(h.id)}
                      >
                        Regresar al agente
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-none"
                      onClick={() => void navigate(`/admin/conversations/${h.id}`)}
                    >
                      Ver traza
                    </Button>
                  </li>
                )
              })}
            </ul>
          </Card>
        )}
      </div>
    </div>
  )
}
